/**
 * Централизованный фасад аналитики.
 * Импортируй { track, identify, reset } из этого файла — не SDK напрямую.
 *
 * Про какой именно провайдер идёт речь, этот файл не знает и знать не должен:
 * AE-инстанс шлёт в PostHog, RU-инстанс — в Яндекс.Метрику, выбор по
 * REACT_APP_ANALYTICS. Вся развилка живёт в analytics/core.js, сюда она не
 * протекает — иначе 1031 вызов в 165 файлах пришлось бы переписывать под
 * второй инстанс. См. metrika_migration_plan.md.
 *
 * В dev-окружении события не отправляются (провайдер сам себя глушит).
 */
import { identifyUser, resetUser, captureEvent } from './analytics/core';

// Идентифицировать пользователя после логина.
// Принимает объект пользователя из /me (id, name, role, subdomain,
// organization_id, org_name, is_demo). Жёстко привязывает аналитику к домену
// ОРГи, из которой пришёл юзер, и изолирует идентичность между тенантами.
export function identify(user) {
  identifyUser(user);
}

// Сбросить сессию при логауте
export function reset() {
  resetUser();
}

// Универсальный трекер событий
export function track(event, properties = {}) {
  captureEvent(event, properties);
}

// ── Конкретные события ──────────────────────────────────────────────────────

export const Analytics = {
  // Авторизация
  login: () => track('user_login'),
  logout: () => track('user_logout'),

  // ICS-фид календаря (Профиль → Уведомления)
  calendarFeedGoogleClicked: () => track('calendar_feed_google_clicked'),
  calendarFeedLinkCopied: () => track('calendar_feed_link_copied'),
  calendarFeedRegenerated: () => track('calendar_feed_regenerated'),
  calendarFeedScopeChanged: (scope) => track('calendar_feed_scope_changed', { scope }), // 'org' | 'mine'

  // Zoho Books (интеграция инвойсов)
  zohoConnectClicked: () => track('zoho_connect_clicked'),
  zohoConnected: () => track('zoho_connected'),
  zohoOrgSelected: () => track('zoho_org_selected'),
  zohoDisconnected: () => track('zoho_disconnected'),
  zohoConnectError: (reason) => track('zoho_connect_error', { reason }),
  zohoBlockToggled: (taskId, opened) => track('zoho_invoice_block_toggled', { task_id: taskId, opened }),
  zohoInvoiceFormOpened: (taskId) => track('zoho_invoice_form_opened', { task_id: taskId }),
  zohoInvoiceCreated: (props = {}) => track('zoho_invoice_created', props), // { task_id, total, items_count, with_expenses }
  zohoInvoiceError: (reason) => track('zoho_invoice_error', { reason }),
  zohoInvoiceSyncClicked: (taskId, statusAfter) => track('zoho_invoice_sync_clicked', { task_id: taskId, status_after: statusAfter }),
  zohoInvoiceOpenedInZoho: (taskId, invoiceId) => track('zoho_invoice_opened_in_zoho', { task_id: taskId, invoice_id: invoiceId }),
  zohoInvoiceVoided: (taskId, invoiceId) => track('zoho_invoice_voided', { task_id: taskId, invoice_id: invoiceId }),
  // Раскрыта расшифровка позиций инвойса. props: { task_id, invoice_id, opened }
  zohoInvoiceItemsToggled: (props = {}) => track('zoho_invoice_items_toggled', props),
  // Синк обнаружил, что сумму инвойса поправили в Zoho. props: { task_id, invoice_id, from, to, source }
  zohoInvoiceTotalChanged: (props = {}) => track('zoho_invoice_total_changed', props),
  // Изменён дефолт орги «выставлять сразу / черновиком». props: { mode: 'sent'|'draft' }
  zohoInvoiceDefaultChanged: (props = {}) => track('zoho_invoice_default_changed', props),
  // Выбран налог организации для позиций инвойса. props: { tax_id }
  zohoTaxChanged: (props = {}) => track('zoho_tax_changed', props),
  // Скачан PDF инвойса из Zoho. props: { task_id, invoice_id }
  zohoInvoicePdfDownloaded: (props = {}) => track('zoho_invoice_pdf_downloaded', props),
  // PDF отдан в нативный «Поделиться» (iOS, второй тап). props: { task_id, invoice_id }
  zohoInvoicePdfShared: (props = {}) => track('zoho_invoice_pdf_shared', props),
  // Счёт отправлен клиенту письмом (шаблон «Коммуникаций» + PDF). props: { task_id, invoice_id, attached }
  zohoInvoiceEmailed: (props = {}) => track('zoho_invoice_emailed', props),
  // Переключён язык письма в превью отправки. props: { doc: 'invoice'|'report', task_id, lang }
  clientEmailLangSwitched: (props = {}) => track('client_email_lang_switched', props),

  // Контроль исполнения (work_facts — блок факта в карточке Работы)
  workFactBlockToggled: (taskId, opened) => track('work_fact_block_toggled', { task_id: taskId, opened }),
  workFactSubmit: (props = {}) => track('work_fact_submit', props), // { task_id, lines_count }
  // Липкий слайдер объёма в тайле строки. Шлётся на отпускание, не на каждый
  // пиксель драга. mode: 'snap' — прилип к засечке, 'free' — остановился между.
  workFactSliderUsed: (props = {}) => track('work_fact_slider_used', props), // { task_id, mode, pct }
  // Свернул блок, не нажав «Провести». Метрика забывчивости: если событий много —
  // напоминание слабое либо кнопка не там, где её ищут.
  workFactUnpostedReminder: (props = {}) => track('work_fact_unposted_reminder', props), // { task_id, lines_count }
  // Разграничение доступа к элементам сметы (quote_assignees).
  //
  // Главный вопрос к фиче — НА КАКОМ УРОВНЕ ею пользуются. Если почти все
  // события идут со scope:'line', значит разделы как единица назначения не
  // работают, и админ расписывает смету построчно — это признак, что нарезать
  // надо не так. Отсюда scope в каждом событии.
  quoteAssigneesModeToggled: (props = {}) => track('quote_assignees_mode_toggled', props),
  // { quote_id, on, frozen } — frozen: назначают до согласования или после
  quoteAssigneeAdded: (props = {}) => track('quote_assignee_added', props),
  // { quote_id, scope: 'quote'|'category'|'line', count }
  quoteAssigneeRemoved: (props = {}) => track('quote_assignee_removed', props),
  // { quote_id, scope, count } — count 0 = назначение снято, вернули наследование
  // Человек упёрся в урезанный доступ: открыл смету и не увидел ни итогов, ни
  // документных кнопок. Частит — значит нарезали слишком мелко либо не тому.
  quoteAccessPartial: (props = {}) => track('quote_access_partial', props), // { quote_id }
  // Сервер отбил проводку по чужой позиции. Ненулевое значение — это либо
  // обход интерфейса, либо расхождение фронта с резолвером: и то и другое
  // нужно видеть сразу.
  workFactLineDenied: (props = {}) => track('work_fact_line_denied', props), // { task_id, line_id }

  // Пикер позиций сметы (WorkFactLinePicker). Воронка: открыл → искал/раскрывал
  // группы → закрыл. Ключевой сигнал — closed с added:0: ушёл ни с чем, значит
  // список непроходим (много смет, длинные категории, поиск не нашёл строку).
  workFactPickerOpened: (props = {}) => track('work_fact_picker_opened', props),
  // { task_id, quote_scope, lines_total, quotes_count, locked_count }
  workFactPickerSearched: (props = {}) => track('work_fact_picker_searched', props),
  // { task_id, results } — одно событие на открытие, на первый непустой запрос
  workFactPickerGroupToggled: (props = {}) => track('work_fact_picker_group_toggled', props),
  // { task_id, level: 'quote'|'category', opened }
  workFactPickerSelectAll: (props = {}) => track('work_fact_picker_select_all', props),
  // { task_id, count, on } — «выбрать всю категорию»
  workFactPickerClosed: (props = {}) => track('work_fact_picker_closed', props),
  // { task_id, added, removed, used_search, picked_in_search, source: 'done'|'backdrop'|'close' }
  // Быстрая доля остатка в тайле (+25% / +50% / Всё). Отличаем от слайдера:
  // если шорткатами закрывают почти всё, слайдер в тайле лишний.
  workFactQuickQty: (props = {}) => track('work_fact_quick_qty', props), // { task_id, pct }
  // Строка добавлена в набор из инлайн-списка (после выпила модалки-пикера).
  workFactLineAddedInline: (props = {}) => track('work_fact_line_added_inline', props),
  // { task_id, in_search } — нашёл поиском или долистал руками
  // Отказ сервера в минусовом списании: метрика дефолта work_facts.correct.
  // Частит — значит роли, которой минус реально нужен, флаг не выдали.
  workFactNegativeDenied: (props = {}) => track('work_fact_negative_denied', props), // { task_id }
  // Крестик на уже проведённой (locked) позиции — удаление факта(ов) списания.
  // failed>0 при deleted>0 — частичное удаление (есть чужие отметки при узкой роли).
  workFactLineRemoved: (props = {}) => track('work_fact_line_removed', props), // { task_id, quote_line_id, deleted, failed }

  // Перерасход: сборка сметы и перенос факта
  // Кнопка показана — знаменатель воронки. Без неё «создали 3 сметы» ни о чём.
  overrunQuoteOffered: (props = {}) => track('overrun_quote_offered', props), // { scope, lines_count }
  overrunQuoteCreated: (props = {}) => track('overrun_quote_created', props), // { scope, lines_count, total_minor }
  // Предложение переноса. source отличает путь согласования: 'approve' — админ
  // жмёт кнопку, 'banner' — смету согласовал клиент на публичной странице.
  overrunTransferOffered: (props = {}) => track('overrun_transfer_offered', props), // { quote_id, pairs, source }
  overrunTransferApplied: (props = {}) => track('overrun_transfer_applied', props),
  // { quote_id, pairs, qty_total, weak_kept, source } — weak_kept: сколько пар
  // с confidence 'name' человек оставил включёнными. Мало — матчинг по имени
  // людям не нужен, много — можно поднимать его доверие.
  overrunTransferDismissed: (props = {}) => track('overrun_transfer_dismissed', props), // { quote_id, pairs, source }

  // Вкладка контроля s3 (ProjectControlPanel)
  projectControlView: (projectId) => track('project_control_view', { project_id: projectId }),
  projectControlTab: (projectId, tab) => track('project_control_tab', { project_id: projectId, tab }), // 'vol'|'mar'|'tim'
  // Импорт сметы из Excel (QuoteImportWizard)
  quoteImportOpened: () => track('quote_import_opened'),
  // Разбор файла на бэке. red — сколько таблиц не сошлись по контрольной сумме;
  // это главная метрика качества импорта, по ней видно, на каких форматах
  // рентген врёт, без разбора самих файлов тенанта.
  quoteImportAnalyzed: (props = {}) => track('quote_import_analyzed', props), // { kind, blocks, lines, red }
  quoteImportUnitsAdded: (props = {}) => track('quote_import_units_added', props), // { n }
  // quote_import_matched убран вместе с ручным шагом «Сопоставить с прайсом»:
  // матчинг теперь делает бэк при разборе, отдельного действия тенанта нет.
  // Исторические события в PostHog остаются, дашборды по ним не ломаются.
  quoteImportAddToPrice: () => track('quote_import_add_to_price'),
  quoteImportCommitted: (props = {}) => track('quote_import_committed', props), // { count }

  // ── Сметы на Работу вне проекта (TaskQuotesSection) + нумерация документов ──
  // Воронка, которую меряем: раскрыл аккордеон → создал смету → отправил клиенту
  // → согласовал → номер присвоен. Обрыв между «отправил» и «согласовал» = смета
  // не доходит до клиента; обрыв на номере = Zoho отваливается.
  quoteAccordionOpened: (props = {}) => track('quote_accordion_opened', props),     // { task_id, count }
  quoteCreateOpened: (props = {}) => track('quote_create_opened', props),           // { place: 'task' }
  quoteApprovedByAdmin: (props = {}) => track('quote_approved_by_admin', props),    // { channel, place }
  quoteDocNumberAssigned: (props = {}) => track('quote_doc_number_assigned', props),// { source: 'zoho'|'internal', retry? }
  quoteManualCharged: (props = {}) => track('quote_manual_charged', props),         // { place } — ручное начисление по смете
  quoteCancelled: (props = {}) => track('quote_cancelled', props),                  // { place }
  quoteInvoiceIssued: (props = {}) => track('quote_invoice_issued', props),        // { place }

  // Чек-листы (библиотека шаблонов инспекций)
  checklistBuilderOpened: (mode) => track('checklist_builder_opened', { mode }), // 'new' | 'edit'
  checklistTemplateCreated: () => track('checklist_template_created'),
  checklistTemplateUpdated: () => track('checklist_template_updated'),
  checklistTemplateDeleted: () => track('checklist_template_deleted'),
  checklistCategoryAdded: () => track('checklist_category_added'),
  checklistItemAdded: () => track('checklist_item_added'),
  checklistStageSet: (stage) => track('checklist_stage_set', { stage: stage || 'none' }),
  checklistCategoryTypesSet: (count) => track('checklist_category_types_set', { count }),
  checklistItemKindSet: (kind) => track('checklist_item_kind_set', { kind }),

  // Email sender (BYO Resend / Fixty)
  emailByoConnectClicked: () => track('email_byo_connect_clicked'),
  emailByoConnected: () => track('email_byo_connected'),
  emailByoConnectError: (reason) => track('email_byo_connect_error', { reason }),
  emailByoDisconnected: () => track('email_byo_disconnected'),
  emailModeChanged: (mode) => track('email_mode_changed', { mode }), // 'fixty' | 'byo'
  emailTestSent: (mode) => track('email_test_sent', { mode }),

  // Вкладка «Интеграции» (хаб Zoho / Do Do / Email / Noxty / Mamo)
  integrationsTabViewed: () => track('integrations_tab_viewed'),

  // Клиентские письма: тема и превью (вкладка «Коммуникации»)
  commsTabViewed: () => track('comms_tab_viewed'),
  commsTemplatePreviewed: (template, lang) => track('comms_template_previewed', { template, lang }),
  commsBrandingSaved: (props = {}) => track('comms_branding_saved', props), // { accent, show_logo }
  commsTextsSaved: (template, lang, props = {}) => track('comms_texts_saved', { template, lang, ...props }),
  commsTestSent: (template, lang) => track('comms_test_sent', { template, lang }),

  // Задачи
  taskCreated: (props = {}) => track('task_created', props),
  taskUpdated: (props = {}) => track('task_updated', props),
  taskStatusChanged: (from, to, taskId) => track('task_status_changed', { from, to, ...(taskId ? { task_id: taskId } : {}) }),
  taskDeleted: (taskId) => track('task_deleted', { task_id: taskId }),

  // Фото
  // Флоу выбора файла. Размечен пошагово, потому что провал бывает молчаливым:
  // Chrome может отказать в показе системного пикера без единой ошибки, и без
  // этих событий воронка обрывается в никуда.
  // opened → source_clicked → files_received. Если вместо третьего пришло
  // no_response — пикер не стартовал (нет user activation / нет обработчика).
  photoPickerOpened: (platform, acceptPdf = false) => track('photo_picker_opened', { platform, accept_pdf: acceptPdf }),
  photoPickerSourceClicked: (source) => track('photo_picker_source_clicked', { source }), // camera|gallery|files
  photoPickerFilesReceived: (source, count) => track('photo_picker_files_received', { source, count }),
  photoPickerNoResponse: (source) => track('photo_picker_no_response', { source }),

  // Служебная информация Объекта.
  // Размечено не «нажали кнопку», а гипотезы самой фичи:
  //   downloaded с source=task около нуля → агрегация файлов Работ людям не
  //     нужна, во второй итерации сворачиваем её в ссылку на Работу;
  //   visibility_toggled на каждом файле → точечный доступ не нужен, Бригадиру
  //     надо просто выдать properties.see_documents (дефолты подобраны неверно);
  //   upload_failed с reason=type → какие форматы реально приносят люди. Белый
  //     список составлен из головы, а не из данных.
  propertyDocsToggled:       (propertyId, expanded) => track('property_docs_toggled', { property_id: propertyId, expanded }),
  propertyDocsUploaded:      (mime, sizeKb)         => track('property_docs_uploaded', { mime, size_kb: sizeKb }),
  propertyDocsDeleted:       (mime)                 => track('property_docs_deleted', { mime }),
  propertyDocsDownloaded:    (source, mime)         => track('property_docs_downloaded', { source, mime }), // own|task
  propertyDocsTaskOpened:    (taskId)               => track('property_docs_task_opened', { task_id: taskId }),
  propertyDocsVideosOpened:  (count)                => track('property_docs_videos_opened', { count }),
  propertyDocsUploadFailed:  (reason)               => track('property_docs_upload_failed', { reason }), // type|size|limit|network
  propertyDocsVisibilityToggled: (docId, visible)   => track('property_docs_visibility_toggled', { doc_id: docId, visible }),

  photoUploaded: (type, count = 1) => track('photo_uploaded', { type, count }),
  photoDeleted: (type) => track('photo_deleted', { type }),   // вызывается в PhotosSection → onDelete
  photoReordered: (type, count) => track('photo_reordered', { type, count }),
  // Ретрай после неудачного GET /tasks/:id/photos. Событие частое = у тенанта
  // проблемы с сетью/R2, а не единичный сбой — иначе это видно только по тикетам.
  photosReloadClicked: () => track('photos_reload_clicked'),

  // Акт (PDF)
  reportGenerated: () => track('report_generated'),
  reportShared: () => track('report_shared'),

  // Объекты
  propertyViewed: () => track('property_viewed'),
  propertyCreated: () => track('property_created'),
  propertyGeolocated: (source) => track('property_geolocated', { source }), // source: 'saved' | 'gps' | 'fallback'
  propertyMapConfirmed: (hasAddress) => track('property_map_confirmed', { has_address: hasAddress }),
  propertyCoverUploaded: (propertyId) => track('property_cover_uploaded', { property_id: propertyId }),
  propertyCoverRemoved: (propertyId) => track('property_cover_removed', { property_id: propertyId }),
  // orientation = landscape | square | portrait: показывает, какие кадры реально
  // грузят тенанты, и стоит ли докручивать раскладку под вертикальные.
  propertyCoverOpened: (propertyId, orientation) => track('property_cover_opened', { property_id: propertyId, orientation }),

  // Удаление Объекта — разрушительное и необратимое (каскадом уходят Работы,
  // фото, Проекты). Меряем воронку целиком: сколько раз открыли модалку и
  // сколько отвалилось, увидев объём последствий. Высокая доля cancelled с
  // большим tasks_count = люди реально не понимали, что удаляют.
  propertyDeleteModalOpened: (props = {}) => track('property_delete_modal_opened', props), // { property_id, place: 'list'|'detail' }
  propertyDeleteCancelled: (props = {}) => track('property_delete_cancelled', props),      // { property_id, place, tasks_count, typed }
  propertyDeleteConfirmed: (props = {}) => track('property_delete_confirmed', props),      // { property_id, place, tasks_count, photos_count, projects_count }
  propertyDeleteFailed: (props = {}) => track('property_delete_failed', props),            // { property_id, place, reason }

  // Удаление / архивация Клиента. Клиента с финансовой историей (счета, AMC,
  // платежи) удалить нельзя — модалка переключается в режим архивации.
  // Смотрим: как часто вообще пытаются удалить контрагента с историей
  // (can_hard_delete=false при открытии) и не бросают ли на этом шаге.
  clientDeleteModalOpened: (props = {}) => track('client_delete_modal_opened', props), // { client_id, place: 'list'|'detail', can_hard_delete, properties_count }
  clientDeleteCancelled: (props = {}) => track('client_delete_cancelled', props),      // { client_id, place, mode: 'delete'|'archive', typed }
  clientDeleteConfirmed: (props = {}) => track('client_delete_confirmed', props),      // { client_id, place, properties_count, tasks_count }
  clientArchived: (props = {}) => track('client_archived', props),                     // { client_id, place, reason: 'blocked'|'chosen' }
  clientUnarchived: (props = {}) => track('client_unarchived', props),                 // { client_id, place }
  clientDeleteFailed: (props = {}) => track('client_delete_failed', props),            // { client_id, place, reason }
  clientsArchivedTabOpened: () => track('clients_archived_tab_opened'),

  // Map View
  mapGeolocateUsed: () => track('map_geolocate_used'),                          // нажал "Моё местоположение" на карте объектов
  propertiesViewChanged: (mode) => track('properties_view_changed', { mode }), // mode: 'list' | 'grid' | 'map'
  mapMarkerClicked: (props = {}) => track('map_marker_clicked', props),        // property_id, property_type
  mapPropertyOpened: (propertyId) => track('map_property_opened', { property_id: propertyId }), // открыл объект из InfoWindow
  mapClusterClicked: (count) => track('map_cluster_clicked', { cluster_size: count }), // кликнул по кластеру

  // Клиенты
  clientViewed: () => track('client_viewed'),
  clientCreated: () => track('client_created'),

  // Поздравление клиента с Днём рождения
  birthdayGreetingOpened: (props = {}) => track('birthday_greeting_opened', props),             // { client_id, already_greeted }
  birthdayGreetingSent: (props = {}) => track('birthday_greeting_sent', props),                 // { client_id, lang, forced, promo }
  birthdayGreetingDuplicateWarned: (props = {}) => track('birthday_greeting_duplicate_warned', props), // { client_id }

  // Клиентские письма из Работы (completion / invoice)
  clientEmailOpened: (props = {}) => track('client_email_opened', props),                 // { task_id, template, already_sent }
  clientEmailSent: (props = {}) => track('client_email_sent', props),                      // { task_id, template, lang, forced }
  clientEmailDuplicateWarned: (props = {}) => track('client_email_duplicate_warned', props), // { task_id, template }
  // Режим напоминаний визита
  visitReminderConfigured: (props = {}) => track('visit_reminder_configured', props),      // { timer, value, notify_client }

  // Голосовой ввод клиента
  voiceClientStarted: () => track('voice_client_started'),
  voiceClientSuccess: (method) => track('voice_client_success', { method }), // method: 'speech_api' | 'fallback'
  voiceClientRetry: (method) => track('voice_client_retry', { method }),

  // Импорт контактов из vCard (десктоп)
  vcfImportStarted: () => track('vcf_import_started'),                                            // открыл файловый пикер .vcf
  vcfImportSuccess: (count, method, photosCount = 0) => track('vcf_import_success', { contacts_count: count, method, photos_count: photosCount }), // method: 'picker' | 'drop'
  vcfImportError: (method) => track('vcf_import_error', { method }),                              // не удалось распарсить .vcf ('picker' | 'drop' | 'modal')
  clientModalContactImport: (source, hasPhoto = false) => track('client_modal_contact_import', { source, has_photo: hasPhoto }), // 'picker_api' | 'vcf'

  // Центр уведомлений
  notifBellOpened: (props = {}) => track('notif_bell_opened', props),       // открыл ЦУ (unread_count)
  notifClicked: (props = {}) => track('notif_clicked', props),               // кликнул на уведомление (event_type, was_unread)
  notifMarkAllRead: () => track('notif_mark_all_read'),                      // "Прочитать все"

  // Работник
  workerStartedTask: (taskId) => track('worker_started_task', { task_id: taskId }), // нажал "Приступил к работе"
  workerPausedTask: (taskId) => track('worker_paused_task', { task_id: taskId }),    // нажал "Приостановить"
  workerResumedTask: (taskId) => track('worker_resumed_task', { task_id: taskId }),  // нажал "Возобновить"

  // Менеджер отметил чужого исполнителя «в работе / не в работе».
  // action приходит с бэкенда ('start' | 'pause' | 'resume') — фронт его не
  // угадывает. Доля start здесь против worker_started_task показывает, насколько
  // бригада вообще жмёт кнопку сама.
  managerMarkedAssignee: (taskId, assigneeId, action) =>
    track('manager_marked_assignee_status', { task_id: taskId, assignee_id: assigneeId, action }),

  // AI Support Chat
  supportQuestionSent: (props = {}) => track('support_question_sent', props), // { question_length, history_length }
  supportAnswerReceived: () => track('support_answer_received'),
  supportError: (error) => track('support_error', { error }),
  supportFeedbackGiven: (props = {}) => track('support_feedback_given', props), // { rating: 'up'|'down', question_length }

  // Demo A0 flow
  demoEmailSubmitted: () => track('demo_email_submitted'),
  demoCodeSent: (props = {}) => track('demo_code_sent', props),   // { email_domain }
  demoCodeSubmitted: () => track('demo_code_submitted'),
  demoStarted: (props = {}) => track('demo_started', props),       // { organization_id }
  demoEmailChangeAttempted: () => track('demo_email_change_attempted'),
  demoBannerCtaClicked: (props = {}) => track('demo_banner_cta_clicked', props),    // "Начать бесплатно" (опц. source)
  demoExited: () => track('demo_exited'),
  demoLanguageSwitched: (lang) => track('demo_language_switched', { lang }),

  // Demo timer / warning modal (5 min before expiry)
  demoWarningShown: (props = {}) => track('demo_warning_shown', props),       // { extended }
  demoExtendClicked: () => track('demo_extend_clicked'),
  demoExtendSucceeded: () => track('demo_extend_succeeded'),
  demoExtendFailed: (props = {}) => track('demo_extend_failed', props),       // { error }
  demoForceLogout: () => track('demo_force_logout'),                          // авто-завершение на 0:00

  // Demo tour (режиссура первых минут)
  demoTourStarted: () => track('demo_tour_started'),
  demoTourSkipped: (props = {}) => track('demo_tour_skipped', props),         // { at_step }
  demoTourStepViewed: (step) => track('demo_tour_step_viewed', { step }),     // 'welcome'|'photos'|'act'|'preview'
  demoTaskAssigned: () => track('demo_tour_task_assigned'),                   // Слайс 0: «Назначить работу»
  demoTourPhotoAdded: (props = {}) => track('demo_tour_photo_added', props),    // юзер сам загрузил фото на шаге тура { photo_type }
  demoTourActOpened: () => track('demo_tour_act_opened'),                      // клик "Сформировать акт" в туре
  demoTourActPreviewed: () => track('demo_tour_act_previewed'),               // инлайн-превью акта показано
  demoTourActShared: () => track('demo_tour_act_shared'),                     // экспорт/шеринг акта из демо
  // Слайс 2: предложение инвойса в ЦУ → оплата
  demoInvoiceOfferShown: () => track('demo_tour_invoice_offer_shown'),        // оффер «выставить инвойс» эмитнут в ЦУ
  demoInvoiceAccepted: () => track('demo_tour_invoice_accepted'),             // ✓ — инвойс выставлен
  demoInvoiceDeclined: () => track('demo_tour_invoice_declined'),             // ✗ — отклонено
  demoInvoicePaid: () => track('demo_tour_invoice_paid'),                     // инвойс «оплачен» (демо-стаб)
  demoExpenseOfferShown: () => track('demo_tour_expense_offer_shown'),        // расход прилетел на согласование (слайс 3)
  demoExpenseApproved: () => track('demo_tour_expense_approved'),             // ✓ — расход согласован
  demoExpenseRejected: () => track('demo_tour_expense_rejected'),             // ✗ — расход отклонён
  demoConvertShown: () => track('demo_tour_convert_shown'),                   // показана карточка конверсии (слайс 4)
  demoConvertClicked: () => track('demo_tour_convert_clicked'),              // клик «Зарегистрироваться» из конверсии
  demoConvertDismissed: () => track('demo_tour_convert_dismissed'),          // «ещё осмотрюсь» из конверсии
  demoGuideReopen: () => track('demo_tour_guide_reopen'),                     // кнопка «Гайд» в баннере — вернуться в тур
  demoTourCompleted: () => track('demo_tour_completed'),                      // рельсы пройдены, отпустили

  // Конструктор отчётов
  reportTemplateCreated: (props = {}) => track('report_template_created', props), // { blocks_count, theme, lang }
  reportTemplateUpdated: (props = {}) => track('report_template_updated', props), // { template_id, blocks_count }
  reportTemplateDeleted: (templateId) => track('report_template_deleted', { template_id: templateId }),
  reportTemplateSetDefault: (templateId) => track('report_template_set_default', { template_id: templateId }),
  customReportGenerated: (props = {}) => track('custom_report_generated', props), // { template_id, template_name, lang }
  // Язык документа — параметр генерации, а не свойство шаблона. Три события
  // вокруг него: переключили тоггл, открыли превью, переключили таб в редакторе.
  //   report_lang_switched   { lang, client_lang, place }
  //   template_preview_opened{ template_id, lang, kind }
  //   doc_template_lang_tab  { kind, lang }
  // Отправляются через track() из мест использования — отдельных обёрток не
  // заводим, свойства у них разные и осмысленны только на месте.
  // Провал генерации. photos_count + duration_sec нужны, чтобы отличить
  // «отвалилось по таймауту на тяжёлой Работе» от прикладной ошибки:
  // ровно этот случай ловили на Работе с 58 фото.
  customReportFailed: (props = {}) => track('custom_report_failed', props),       // { template_id, status, duration_sec, reason }
  serviceInfoBlockAdded: (props = {}) => track('service_info_block_added', props), // { template_id }
  // Какие блоки тенанты реально добавляют в шаблон. props: { block_type, template_id }
  reportTemplateBlockAdded: (props = {}) => track('report_template_block_added', props),

  // Сметы / Quotations — прейскурант
  priceListTabOpened: () => track('price_list_tab_opened'),
  priceItemCreated: (props = {}) => track('price_item_created', props),            // { unit }
  priceItemUpdated: (itemId) => track('price_item_updated', { item_id: itemId }),
  priceItemDeleted: (itemId) => track('price_item_deleted', { item_id: itemId }),
  priceCaseCreated: (props = {}) => track('price_case_created', props),            // { pricing_mode, items_count }
  priceCaseUpdated: (props = {}) => track('price_case_updated', props),            // { case_id, items_count }
  priceCaseDeleted: (caseId) => track('price_case_deleted', { case_id: caseId }),
  priceListSearched: (props = {}) => track('price_list_searched', props),          // { has_query, nature, has_category }
  priceCategoryCreated: (props = {}) => track('price_category_created', props),     // { has_parent }
  priceCategoryMoved: (props = {}) => track('price_category_moved', props),          // { to_root }
  priceCategoryMerged: () => track('price_category_merged'),
  priceCategoryDeleted: () => track('price_category_deleted'),
  // Словарь единиц измерения. source различает две точки входа: секция в
  // Прейскуранте и заведение «на лету» из строки сметы — нам важно знать,
  // насколько второй сценарий вообще нужен.
  priceUnitCreated: (props = {}) => track('price_unit_created', props),             // { source, code }
  priceUnitRenamed: (props = {}) => track('price_unit_renamed', props),             // { code }
  priceUnitMerged: (props = {}) => track('price_unit_merged', props),               // { from, to, moved }
  priceUnitDeleted: (props = {}) => track('price_unit_deleted', props),             // { code }
  quoteLineUnitChanged: (props = {}) => track('quote_line_unit_changed', props),    // { unit }
  priceImportDryRun: (props = {}) => track('price_import_dryrun', props),           // { total, new, updated, unchanged, errors }
  priceImportCommitted: (props = {}) => track('price_import_committed', props),     // { new, updated }

  // AMC — Annual Maintenance Contracts
  amcSectionOpened:    (propertyId) => track('amc_section_opened',    { property_id: propertyId }),
  amcContractCreated:  (props = {}) => track('amc_contract_created',  props), // { property_id, items_count }
  amcContractUpdated:  (props = {}) => track('amc_contract_updated',  props), // { contract_id }
  amcContractDeleted:  (contractId) => track('amc_contract_deleted',  { contract_id: contractId }),

  // Доходы — единый блок (ручные начисления + инвойсы интеграций)
  incomeBlockToggled: (taskId, opened) => track('income_block_toggled', { task_id: taskId, opened }),
  // Расходы привязаны к доходу (income_expense_links). props:
  // { task_id, source: 'manual'|'zoho', work_count, object_count, reused_count }
  incomeExpenseLinksSaved: (props = {}) => track('income_expense_links_saved', props),
  // Позиция начисления подставлена из прайс-листа. props: { task_id, price_item_id }
  incomePriceItemPicked: (props = {}) => track('income_price_item_picked', props),
  // Переключён язык превью формы начисления. props: { task_id, lang: 'ru'|'en', cached }
  incomeLangPreviewToggled: (props = {}) => track('income_lang_preview_toggled', props),
  // Текст расхода отредактирован перед попаданием в начисление. props: { task_id, slice: 'task'|'property' }
  incomeExpenseLabelEdited: (props = {}) => track('income_expense_label_edited', props),
  // Черновик начисления сохранён (кнопка «Сохранить»). props: { task_id, mode, items_count, expenses_count }
  incomeDraftSaved: (props = {}) => track('income_draft_saved', props),
  // Форма открыта с восстановленным черновиком. props: { task_id }
  incomeDraftRestored: (props = {}) => track('income_draft_restored', props),
  // Режим начисления Работы изменён (сегмент-контрол блока «Доходы»). props:
  // { task_id, billing_mode: 'accrue'|'amc'|'none', reason_code?, amc_items_count? }
  taskBillingModeChanged: (props = {}) => track('task_billing_mode_changed', props),

  // Финаналитика (ручной флоу «Доходов»; событийные имена сохранены для непрерывности аналитики)
  finanalyticsToggled: (enabled) => track('finanalytics_toggled', { enabled }),                 // тогл в настройках
  finanalyticsBlockToggled: (taskId, opened) => track('finanalytics_block_toggled', { task_id: taskId, opened }),
  finanalyticsEntryCreated: (taskId, amount) => track('finanalytics_entry_created', { task_id: taskId, amount }),
  finanalyticsEntryUpdated: (entryId) => track('finanalytics_entry_updated', { entry_id: entryId }),
  finanalyticsEntryConfirmed: (entryId) => track('finanalytics_entry_confirmed', { entry_id: entryId }),
  finanalyticsEntryUnconfirmed: (entryId) => track('finanalytics_entry_unconfirmed', { entry_id: entryId }),
  finanalyticsEntryDeleted: (entryId) => track('finanalytics_entry_deleted', { entry_id: entryId }),
  finanalyticsReportExported: (format, count) => track('finanalytics_report_exported', { format, count }),

  // Статистика — навигация по таймлайну графика расходов
  statsExpensesTimelineNav: (direction, method) => track('stats_expenses_timeline_nav', { direction, method }), // direction: prev|next, method: arrow|swipe

  // ИИ-советник Дашборда (admin/superadmin)
  advisorViewed: (state) => track('advisor_viewed', { state }), // cold|warming|live
  advisorCardClicked: (card, state) => track('advisor_card_clicked', { card, state }), // card: leak|awaiting|expenses|birthday|docs|warming_invoice|warming_zoho
  advisorLineClicked: (target, state) => track('advisor_line_clicked', { target, state }), // target: leak|awaiting|expenses — клик по цифре в строке-приоритете

  // Мини-празднования — рубежи организации (CelebrationModal).
  // props везде: { metric, threshold, ... }. Разница shown→dismissed по времени
  // покажет, смотрят ли splash или закрывают рефлекторно.
  milestoneShown:      (props = {}) => track('milestone_shown', props),       // { metric, threshold, value_at }
  milestoneDismissed:  (props = {}) => track('milestone_dismissed', props),   // { metric, threshold, source: backdrop|escape }
  milestoneCtaClicked: (props = {}) => track('milestone_cta_clicked', props), // { metric, threshold }

  // Видео в служебной информации Работы.
  //
  // Ключевые метрики фичи — не просмотры, а два провала. video_upload_failed
  // показывает, доезжают ли ролики с объекта по мобильной сети (файл на два
  // порядка тяжелее фото). video_processing_failed — жив ли pm_media: без
  // этого события узнать о его падении можно только от тенанта.
  videoPickerOpened:    () => track('video_picker_opened'),
  // camera|gallery — только Android, где мы показываем свою шторку выбора.
  // Перекос в сторону gallery будет означать, что снимают не в приложении,
  // а присылают готовое (например, переслав себе из мессенджера).
  videoPickerSource:    (source) => track('video_picker_source', { source }),
  videoUploadStarted:   (durationSec, sizeMb) => track('video_upload_started', { duration_sec: durationSec, size_mb: sizeMb }),
  videoUploaded:        (durationSec) => track('video_uploaded', { duration_sec: durationSec }),
  videoUploadFailed:    (reason) => track('video_upload_failed', { reason }),
  videoProcessingFailed:(errorCode) => track('video_processing_failed', { error_code: errorCode }),
  videoPlayed:          (durationSec) => track('video_played', { duration_sec: durationSec }),
  videoDeleted:         (status) => track('video_deleted', { status }),
  videoReordered:       (count) => track('video_reordered', { count }),
  // Переход между роликами в просмотрщике. Если событий много при одном
  // video_played — значит с одной Работы снимают серию, и лимит в три ролика
  // придётся пересмотреть.
  videoNavigated:       (direction) => track('video_navigated', { direction }), // prev|next
  // method: download (десктоп/Android) | share_ready (iOS, показали «Поделиться»)
  // | fallback_open (fetch не прошёл — скорее всего CORS у бакета)
  videoDownloaded:      (method) => track('video_downloaded', { method }),
  // Главная метрика Фазы 2. Доля accept показывает, попадает ли саммари в цель,
  // а edited — правил ли человек текст перед вставкой: много правок значит, что
  // модели не хватает контекста, а не что она бесполезна.
  videoSummaryDecision: (action, edited) => track('video_summary_decision', { action, edited }), // accept|reject
  // Частые открытия расшифровки = саммари не хватает: люди идут проверять
  // исходник. Сигнал к правке промпта, а не к новой кнопке.
  videoTranscriptOpened: () => track('video_transcript_opened'),
  // Пересборка пересказа по той же расшифровке. Много повторов на одно видео
  // значит, что с первого раза саммари не устраивает — смотреть промпт.
  videoSummaryRegenerated: (result) => track('video_summary_regenerated', { result }), // suggested|no_speech

  // ИИ-советник Дашборда Работника (worker)
  workerAdvisorViewed: (state) => track('worker_advisor_viewed', { state }), // warming|live
  workerAdvisorMetricClicked: (key, state) => track('worker_advisor_metric_clicked', { key, state }), // today|overdue|no_photo
  workerAdvisorCardClicked: (card, state) => track('worker_advisor_card_clicked', { card, state }), // overdue|no_photo|new_assignments|warming_first
  workerAdvisorLineClicked: (target, state) => track('worker_advisor_line_clicked', { target, state }), // клик по цифре в строке-приоритете

  // Добавление PWA на экран (Настройки → карточка «Мобильное приложение» +
  // шаг чек-листа онбординга home_screen + Профиль → Уведомления, когда push
  // недоступен на iOS именно из-за режима вкладки). platform различает три
  // ветки useInstallPrompt: 'android' — есть beforeinstallprompt, 'ios' —
  // только инструкция (нативного API нет), 'unsupported' — ни то ни другое
  // (браузер ещё не решил или не поддерживает установку вовсе).
  homeScreenInstallClicked: (props = {}) => track('home_screen_install_clicked', props), // { platform, has_small_logo, source: 'profile'|'checklist'|'notifications'; до 19.10.2026 был 'settings' }
  // outcome приходит только для android: 'accepted'|'dismissed' от userChoice нативного диалога.
  homeScreenInstallResult: (props = {}) => track('home_screen_install_result', props),   // { outcome }
  homeScreenIosInstructionsShown: () => track('home_screen_ios_instructions_shown'),
  homeScreenIosInstructionsClosed: () => track('home_screen_ios_instructions_closed'),
};
